import type { PanelCommand } from '../store/panelTypes';

/**
 * Panel bindings by `KeyboardEvent.code`.
 *
 * `code` is layout-independent, unlike the `keyCode` numbers this replaced, so
 * the bindings stay on the same physical keys on AZERTY and Dvorak. The number
 * row and the QWERTY row drive the panel; the two rows below it play notes, so
 * nothing is bound twice.
 */
export const PANEL_KEYS: Record<PanelCommand, readonly string[]> = {
  Button_AllCancel: ['Escape'],
  Button_Arp_Stop: ['Backspace'],
  Button_Demo_StartStop: ['KeyF'],
  Button_MP_PlayStop: ['Space'],
  Button_MP_Record: ['Enter'],
  Button_Tempo_Down: ['Comma'],
  Button_Tempo_Up: ['Period'],

  Button_DataEntry_Down: ['Minus', 'NumpadSubtract', 'ArrowDown'],
  Button_DataEntry_Up: ['Equal', 'NumpadAdd', 'ArrowUp'],

  Button_Attack: ['Digit1'],
  Button_Decay: ['Digit2'],
  Button_Sustain: ['Digit3'],
  Button_Release: ['Digit4'],

  Button_Loop: ['Digit5'],
  Button_PingPong: ['Digit6'],
  Button_Reverse: ['Digit7'],
  Button_Echo: ['Digit8'],
  Button_Fuzz: ['Digit9'],
  Button_Filter: ['Digit0'],
  Button_Reverb: ['Backslash'],

  Button_Vibrato: ['KeyA'],

  Button_Sample: ['Backquote'],
  Button_Overwrite: ['Slash'],

  Button_ModeSelect_ArpSelect: ['Tab'],

  Button_Select_1: ['KeyQ'],
  Button_Select_2: ['KeyW'],
  Button_Select_3: ['KeyE'],
  Button_Select_4: ['KeyR'],
  Button_Select_5: ['KeyT'],
  Button_Select_6: ['KeyY'],
  Button_Select_7: ['KeyU'],
  Button_Select_8: ['KeyI'],
  Button_Select_9: ['KeyO'],
  Button_Select_10: ['KeyP'],
  Button_Select_11: ['BracketLeft'],
  Button_Select_12: ['BracketRight'],
};

export const PANEL_KEY_MAP: ReadonlyMap<string, PanelCommand> = new Map(
  (Object.entries(PANEL_KEYS) as [PanelCommand, string[]][]).flatMap(([command, codes]) =>
    codes.map((code) => [code, command] as const),
  ),
);

export const WHEEL_COMMANDS = {
  UP: 'Button_DataEntry_Up',
  DOWN: 'Button_DataEntry_Down',
} as const satisfies Record<string, PanelCommand>;

/**
 * Note bindings, tracker-style: the bottom row plays white keys and the home
 * row holds the sharps sitting above them.
 */
export const NOTE_KEYS: ReadonlyMap<string, number> = new Map([
  ['KeyZ', 0],
  ['KeyS', 1],
  ['KeyX', 2],
  ['KeyD', 3],
  ['KeyC', 4],
  ['KeyV', 5],
  ['KeyG', 6],
  ['KeyB', 7],
  ['KeyH', 8],
  ['KeyN', 9],
  ['KeyJ', 10],
  ['KeyM', 11],
  ['KeyK', 13],
  ['KeyL', 15],
  ['Semicolon', 17],
  ['Quote', 19],
]);

/** C4. Keeps every computer-keyboard note inside the 32 modelled keys. */
export const COMPUTER_KEYBOARD_BASE_NOTE = 60;

/** Shifts the computer keyboard by an octave. */
export const OCTAVE_KEYS = { DOWN: 'ArrowLeft', UP: 'ArrowRight' } as const;
